import { Check, Clock, Package, Truck, Home, X } from 'lucide-react';

/**
 * Visual order tracking timeline for customers.
 * Status flow: Pending Payment → Paid → Preparing → Shipped → Completed
 * (Rejected = terminal failure state)
 */
const STAGES = [
  { key: 'Pending Payment', label: 'Order Placed', icon: Clock },
  { key: 'Paid', label: 'Payment Confirmed', icon: Check },
  { key: 'Preparing', label: 'Preparing', icon: Package },
  { key: 'Shipped', label: 'Shipped', icon: Truck },
  { key: 'Completed', label: 'Delivered', icon: Home },
];

const STATUS_INDEX = {
  'Pending Payment': 0,
  'Paid': 1,
  'Preparing': 2,
  'Shipped': 3,
  'Completed': 4,
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-PH', {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function OrderTimeline({ status, statusHistory = [] }) {
  // Special case: rejected order
  if (status === 'Rejected') {
    return (
      <div data-testid="order-timeline-rejected" className="bg-red-500/5 border border-red-500/20 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <X className="w-4 h-4 text-red-400" />
          </div>
          <div>
            <p className="text-red-400 text-sm font-sans font-medium">Order Cancelled</p>
            <p className="text-white/40 text-xs font-sans">This order was unable to proceed.</p>
          </div>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_INDEX[status] ?? 0;

  // Build a quick lookup for history by status key (most recent matching entry)
  const historyByStatus = {};
  for (const entry of statusHistory) {
    historyByStatus[entry.status] = entry;
  }

  return (
    <div data-testid="order-timeline" className="bg-[#141414] border border-white/5 p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-5">
        <div className="w-1 h-4 bg-[#1C7D3F]" />
        <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-sans">Order Progress</p>
      </div>

      {/* Desktop: horizontal stepper */}
      <div className="hidden sm:block">
        <div className="relative flex items-start justify-between">
          {/* connecting line behind */}
          <div className="absolute top-5 left-[5%] right-[5%] h-[2px] bg-white/8" />
          <div
            className="absolute top-5 left-[5%] h-[2px] bg-[#1C7D3F] transition-all duration-700"
            style={{ width: `${(currentIdx / (STAGES.length - 1)) * 90}%` }}
          />
          {STAGES.map((stage, idx) => {
            const reached = idx <= currentIdx;
            const isCurrent = idx === currentIdx;
            const Icon = stage.icon;
            const histEntry = historyByStatus[stage.key];
            return (
              <div
                key={stage.key}
                data-testid={`timeline-stage-${stage.key.toLowerCase().replace(/\s/g, '-')}`}
                className="relative flex flex-col items-center flex-1 z-10"
              >
                <div
                  className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all duration-500 ${
                    reached
                      ? 'bg-[#1C7D3F] border-[#1C7D3F] text-white'
                      : 'bg-[#0A0A0A] border-white/15 text-white/30'
                  } ${isCurrent ? 'ring-4 ring-[#1C7D3F]/20' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                </div>
                <p className={`mt-2.5 text-[11px] font-sans tracking-wide text-center px-1 ${reached ? 'text-white' : 'text-white/30'}`}>
                  {stage.label}
                </p>
                {histEntry && (
                  <p className="text-[9px] text-white/30 font-mono mt-0.5">{formatDate(histEntry.timestamp)}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: vertical stepper */}
      <div className="sm:hidden space-y-3">
        {STAGES.map((stage, idx) => {
          const reached = idx <= currentIdx;
          const isCurrent = idx === currentIdx;
          const Icon = stage.icon;
          const histEntry = historyByStatus[stage.key];
          return (
            <div
              key={stage.key}
              data-testid={`timeline-stage-mobile-${stage.key.toLowerCase().replace(/\s/g, '-')}`}
              className="flex items-start gap-3"
            >
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-colors ${
                    reached
                      ? 'bg-[#1C7D3F] border-[#1C7D3F] text-white'
                      : 'bg-[#0A0A0A] border-white/15 text-white/30'
                  } ${isCurrent ? 'ring-2 ring-[#1C7D3F]/30' : ''}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </div>
                {idx < STAGES.length - 1 && (
                  <div className={`w-[2px] h-7 ${idx < currentIdx ? 'bg-[#1C7D3F]' : 'bg-white/8'}`} />
                )}
              </div>
              <div className="flex-1 pt-1.5">
                <p className={`text-sm font-sans ${reached ? 'text-white' : 'text-white/40'}`}>
                  {stage.label}
                </p>
                {histEntry && (
                  <p className="text-[10px] text-white/30 font-mono mt-0.5">{formatDate(histEntry.timestamp)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
